import React, { useEffect, useState } from "react";
import { RequestInputData } from "src/model/RequestInputData";
import SearchForm from "../SearchForm/SearchForm";
import { Route, Switch, useHistory, useLocation } from "react-router-dom";
import ResultsTable from "../ResultsTable/ResultsTable";
import ImageViewer from "../ImageViewer/ImageViewer";
import {
    HARDWARE_SOURCE_SET_SUPPORT_INCOMPLETE_CAPTURE,
    REGION_HARDWARE_SOURCE_MAP
} from "../SearchForm/constant";
import {getItemCaptures} from "src/api/Api";

/**
 * This container is used for the high level layout components on SampleItemImages page.
 * This includes: Search form
 *                Result Table
 *                Image View
 */
const SampleItemImages: React.FunctionComponent = () => {
    // Making the initial state of itemCapturesData to be undefined instead of empty list to be able to
    // distinguish between initial state and empty list returned by API when there are no captures for item
    const [itemCapturesData, setItemCapturesData] = useState<Array<any>>();
    const [captureToDisplay, setCaptureToDisplay] = useState();
    const [displayResultsTableProgressBar, setDisplayResultsTableProgressBar] = useState<boolean>(false);
    const [displayResultsTableErrorMessage, setDisplayResultsTableErrorMessage] = useState<boolean>(false);
    const [inputData, setInputData] = useState<RequestInputData>();

    const history = useHistory();
    const location = useLocation();

    useEffect(() => {
        const searchQuery = new URLSearchParams(location.search);

        // use the inbuilt methods to get the value from searchQuery
        const itemId = searchQuery.get("itemId")
        const itemIdType = searchQuery.get("itemIdType")
        const hardwareSource = searchQuery.get("hardwareSource")
        const sensorType = searchQuery.get("sensorType")
        const includeIncompleteCaptures = searchQuery.get("includeIncompleteCaptures")
        const region =  searchQuery.get("region")

        // update state and fetch data only when all query parameter provided
        if (itemId && itemIdType && hardwareSource && sensorType && includeIncompleteCaptures && region) {
            let inputDataParams = {
                itemId: itemId,
                itemIdType: itemIdType,
                hardwareSource: hardwareSource,
                sensorType: sensorType,
                includeIncompleteCaptures: includeIncompleteCaptures == 'true',
                region: region
            }
            setInputData(inputDataParams);

            const fetchData = async () => {
                setDisplayResultsTableProgressBar(true);
                try {
                    let itemCapturesResult: Array<any> = [];
                    await fetchItemCaptures(inputDataParams, itemCapturesResult);
                    setItemCapturesData(itemCapturesResult);
                    setDisplayResultsTableErrorMessage(false);
                }
                catch (e) {
                    setItemCapturesData(undefined);
                    setDisplayResultsTableErrorMessage(true);
                }
                setDisplayResultsTableProgressBar(false);
            };
            fetchData();
        }
    },[])

    const fetchItemCaptures = async (inputData: RequestInputData, itemCapturesResult: Array<any>) => {
        const {region, ...getItemCapturesRequestBody} = inputData;

        if (inputData.hardwareSource === "ALL") {
            const {sensorType, ...requestBodyWithoutSensorType} = getItemCapturesRequestBody;

            const regionTyped = inputData.region as keyof typeof REGION_HARDWARE_SOURCE_MAP;
            let hardwareSourceArray =  REGION_HARDWARE_SOURCE_MAP[regionTyped];
            for ( var i = 1; i < hardwareSourceArray.length; i++ ) {
                const requestData = Object.assign({}, requestBodyWithoutSensorType, {hardwareSource: hardwareSourceArray[i].value});

                if (!HARDWARE_SOURCE_SET_SUPPORT_INCOMPLETE_CAPTURE.includes(requestData.hardwareSource) && requestData.includeIncompleteCaptures) {
                    delete requestData.includeIncompleteCaptures;
                }

                let responseData = await getItemCaptures(inputData.region, requestData);
                const itemCapturesWithHardwareSource = responseData.captureList
                    .map((capture: any) => (
                        {hardwareSource: hardwareSourceArray[i].value, ...capture}
                    ));
                itemCapturesResult.push(...itemCapturesWithHardwareSource);
            }
        } else {
            if(inputData.sensorType === "ALL") {
                delete getItemCapturesRequestBody.sensorType;
            }

            let responseCaptures = await getItemCaptures(inputData.region, getItemCapturesRequestBody)
            const itemCapturesWithHardwareSource = responseCaptures.captureList
                .map((capture: any) => (
                    {hardwareSource: inputData.hardwareSource, ...capture}
                ));
            itemCapturesResult.push(...itemCapturesWithHardwareSource);
        }
    };

    const onSearch = async (inputData: RequestInputData ) => {
        let path = "/sampleItemImages/search?region=" +
            inputData.region +
            "&hardwareSource=" +
            inputData.hardwareSource +
            "&sensorType=" +
            inputData.sensorType +
            "&includeIncompleteCaptures=" +
            inputData.includeIncompleteCaptures +
            "&itemIdType=" +
            inputData.itemIdType +
            "&itemId=" +
            inputData.itemId;

        history.push(path);

        setDisplayResultsTableProgressBar(true);
        try {
            let itemCapturesResult: Array<any> = [];
            await fetchItemCaptures(inputData, itemCapturesResult);
            setItemCapturesData(itemCapturesResult);
            setDisplayResultsTableErrorMessage(false);
        }
        catch (e) {
            setItemCapturesData(undefined);
            setDisplayResultsTableErrorMessage(true);
        }
        setInputData(inputData);
        setDisplayResultsTableProgressBar(false);
        setCaptureToDisplay(undefined);
    };

    return(
        <>
        <SearchForm initialInputData={inputData!} onSubmit={onSearch} />
            <Switch>
                <Route path="/sampleItemImages/search">
                    <ResultsTable
                        itemCapturesData={itemCapturesData}
                        inputData={inputData!}
                        displayProgressBar={displayResultsTableProgressBar}
                        displayErrorMessage={displayResultsTableErrorMessage}
                        setCaptureToDisplay={setCaptureToDisplay}
                        setDisplayProgressBar={setDisplayResultsTableProgressBar}
                    />
                    <ImageViewer
                        captureToDisplay={captureToDisplay}
                        inputData={inputData!}
                    />
                </Route>
            </Switch>
        </>
    );
}

export default SampleItemImages;
